import React, { useMemo } from 'react'
import { isEmpty } from 'lodash'
import type {
  CurrentTargetAudience,
  OnlineAuctionSegment,
  OnlineAuctionSegmentBidRank,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import { ColorNames } from '../../../../../constants'
import moment from 'moment'
import { match as matchTsPattern, P } from 'ts-pattern'
import { Ribbon } from '../../../../elementals'
import { bidding } from '@adc/blueprint'

const getExpirationParts = (expirationDate: moment.Moment) => {
  const duration = moment.duration(expirationDate.diff(moment()))
  const days = Math.floor(duration.asDays())
  const hours = duration.hours()
  const minutes = duration.minutes()
  return {
    dayPart: days > 0 ? `${days}d ` : '',
    hourPart: hours > 0 ? `${hours}hr ` : '',
    minutePart: minutes > 0 ? `${minutes}m` : '',
  }
}

const getRibbonContent = (
  bidRank: OnlineAuctionSegmentBidRank | undefined,
  viewingFrom: CurrentTargetAudience = graph.enums.CurrentTargetAudience.Seller,
  isEnglishType: boolean = false,
  segment?: OnlineAuctionSegment
) => {
  const bid = bidRank?.bid
  if (!bid) return null
  const isBuyerView = viewingFrom === graph.enums.CurrentTargetAudience.Buyer
  const bidTermStatus = bid?.terms?.status
  const bidUpdated = (bid?.version ?? 0) > 0
  const counteroffer = bidding.utils.onlineAuctionUtils.getCounterofferBid(
    segment as OnlineAuctionSegment,
    bid
  )
  const expirationDate = counteroffer?.terms?.expiration_date
    ? moment(counteroffer.terms.expiration_date)
    : null
  const belongsToCurrentUser = bidRank?.belongs_to_current_user

  // @ts-ignore waiting for upgrade to ts 5
  return matchTsPattern({
    isEnglishType,
    isBuyerView,
    bidTermStatus,
    bidUpdated,
    counteroffer,
    belongsToCurrentUser,
  })
    .with(
      {
        isEnglishType: false,
        bidTermStatus: graph.enums.SealedBidStatus.SellerAccepted,
      },
      () => ({
        title: isBuyerView ? 'Offer Accepted!' : 'Offer Accepted',
        color: ColorNames.AdcDarkGreen,
        icon_name: 'handshake',
      })
    )
    .with(
      {
        isEnglishType: false,
        bidTermStatus: graph.enums.SealedBidStatus.SellerRejected,
      },
      () => ({
        title: 'Offer Declined',
        color: ColorNames.Error,
        icon_name: 'comment-xmark',
      })
    )
    .with(
      {
        isEnglishType: false,
        bidTermStatus: graph.enums.SealedBidStatus.BuyerAccepted,
      },
      () => ({
        title: isBuyerView ? 'Offer Accepted!' : 'Buyer Accepted',
        color: ColorNames.AdcDarkGreen,
        icon_name: 'handshake',
      })
    )
    .with(
      {
        isEnglishType: false,
        isBuyerView: false,
        bidTermStatus: graph.enums.SealedBidStatus.Unset,
        bidUpdated: false,
      },
      () => ({
        title: 'New Offer',
        color: ColorNames.Info,
        icon_name: 'bell',
      })
    )
    .with(
      {
        isEnglishType: false,
        isBuyerView: false,
        bidTermStatus: graph.enums.SealedBidStatus.Unset,
        bidUpdated: true,
      },
      () => ({
        title:
          viewingFrom === graph.enums.CurrentTargetAudience.Seller
            ? 'Offer Updated By Buyer'
            : 'Offer Updated',
        color: ColorNames.Info,
        icon_name: 'bell',
      })
    )
    .with(
      {
        isEnglishType: false,
        isBuyerView: false,
        bidTermStatus: graph.enums.SealedBidStatus.ReSubmitted,
      },
      () => ({
        title: 'New Offer',
        color: ColorNames.Info,
        icon_name: 'bell',
      })
    )
    .with(
      {
        isEnglishType: false,
        bidTermStatus: graph.enums.SealedBidStatus.BuyerRejected,
      },
      () => ({
        title: isBuyerView ? 'Offer Declined' : 'Buyer Declined',
        color: ColorNames.Error,
        icon_name: 'comment-xmark',
      })
    )
    .with(
      {
        isEnglishType: false,
        bidTermStatus: graph.enums.SealedBidStatus.SellerCounterRejected,
        isBuyerView: false,
      },
      () => ({
        title: 'Buyer Standing Firm',
        color: ColorNames.AdcLightGreen,
        icon_name: 'circle-minus',
      })
    )
    .with(
      {
        isEnglishType: false,
        isBuyerView: false,
        bidTermStatus: graph.enums.SealedBidStatus.SellerCountered,
        counteroffer: P.when(counteroffer => !!counteroffer),
      },
      () => {
        let suffix = ''
        if (expirationDate) {
          const { dayPart, hourPart, minutePart } =
            getExpirationParts(expirationDate)
          suffix = `${dayPart}${hourPart}${minutePart}`
        }
        return {
          title: `Your counteroffer expires in ${
            isEmpty(suffix) ? '< 1 min' : suffix
          }`,
          color: ColorNames.AdcLightGreen,
          icon_name: 'comment-dollar',
        }
      }
    )
    .with(
      {
        isEnglishType: false,
        isBuyerView: false,
        bidTermStatus: graph.enums.SealedBidStatus.SellerCountered,
        counteroffer: P.when(counteroffer => !counteroffer),
      },
      () => {
        return {
          title: `Counter Expired`,
          color: ColorNames.Error,
          icon_name: 'circle-exclamation',
        }
      }
    )
    .with(
      {
        isEnglishType: false,
        isBuyerView: false,
        bidTermStatus: graph.enums.SealedBidStatus.BuyerCountered,
        counteroffer: P.when(counteroffer => !!counteroffer),
      },
      () => {
        let suffix = ''
        if (expirationDate) {
          const { dayPart, hourPart, minutePart } =
            getExpirationParts(expirationDate)
          suffix = ` ${dayPart}${hourPart}${minutePart}`
        }
        return {
          title: `Buyer Countered ${suffix}`,
          color: ColorNames.AdcLightGreen,
          icon_name: 'comment-dollar',
        }
      }
    )
    .with(
      {
        isEnglishType: false,
        isBuyerView: false,
        bidTermStatus: graph.enums.SealedBidStatus.BuyerCountered,
        counteroffer: P.when(counteroffer => !counteroffer),
      },
      () => {
        return {
          title: `Counter Expired`,
          color: ColorNames.Error,
          icon_name: 'circle-exclamation',
        }
      }
    )
    .with(
      {
        isEnglishType: false,
        isBuyerView: true,
        counteroffer: P.when(counteroffer => !!counteroffer),
        belongsToCurrentUser: true,
      },
      () => {
        return {
          title: 'Counteroffer Received!',
          color: ColorNames.AdcLightGreen,
          icon_name: 'comment-dollar',
        }
      }
    )
    .otherwise(() => null)
}

type BidRankRibbonProps = {
  bidRank: OnlineAuctionSegmentBidRank
  viewingFrom: CurrentTargetAudience
  isEnglishType?: boolean
  segment?: OnlineAuctionSegment
}
export const BidRankRibbon = ({
  bidRank,
  viewingFrom,
  isEnglishType,
  segment,
}: BidRankRibbonProps) => {
  const ribbonContent = useMemo(
    () => getRibbonContent(bidRank, viewingFrom, isEnglishType, segment),
    [isEnglishType, bidRank, viewingFrom, segment]
  )
  if (!ribbonContent) return null
  return (
    <Ribbon
      color={ribbonContent.color}
      size='large'
      style={{
        position: 'absolute',
        top: -12,
        left: -17,
        zIndex: 2,
      }}
      elmId={`${ribbonContent.title
        .toLowerCase()
        .replace(/\s+/g, '-')}-ribbon-${segment?.listing_id || ''}`}
    >
      <Ribbon.Content
        title={ribbonContent.title}
        iconProps={{
          name: ribbonContent.icon_name,
          color: 'white',
        }}
      />
    </Ribbon>
  )
}

export default BidRankRibbon
